import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { isMobile } from '../utils/mobileUtils';
import type { DirectMessage } from '../store/messageStore';
import { useMessageStore } from '../store/messageStore';
import { useAuthStore } from '../store/authStore';
import { useSocket } from '../contexts/SocketContext';
import { userService } from '../services/userService';
import {
  ConversationList,
  ConversationHeader,
  DirectMessageBubble,
  DirectMessageInput,
  MessageRestrictionWarning
} from '../components/messages';
import { LoadingSpinner } from '../components/ui';

// Group messages by user and time proximity for DMs
const groupConsecutiveMessages = (messages: DirectMessage[]) => {
  const TIME_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds
  
  return messages.map((message, index) => {
    const prevMessage = messages[index - 1];
    
    // Is this message part of a group (consecutive from same user within time threshold)?
    const isGrouped = prevMessage && 
      prevMessage.senderId === message.senderId &&
      (new Date(message.sentAt).getTime() - new Date(prevMessage.sentAt).getTime()) < TIME_THRESHOLD;
    
    return {
      ...message,
      isGrouped, // Don't show timestamp/username if grouped
      isGroupLeader: !isGrouped // Show timestamp/username if this starts a new group
    };
  });
};

const MessagesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showMobileConversation, setShowMobileConversation] = useState(false);
  const [newConversationUser, setNewConversationUser] = useState<{id: string, username: string, profilePhoto: string | null} | null>(null);
  const [isLoadingNewUser, setIsLoadingNewUser] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const selectedUserId = searchParams.get('user');
  const { user } = useAuthStore();
  const { isConnected } = useSocket();
  
  // Refs for auto-scrolling
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const mobileMessagesContainerRef = useRef<HTMLDivElement>(null);

  // Function to scroll to bottom
  const scrollToBottom = () => {
    const container = showMobileConversation ? mobileMessagesContainerRef.current : messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  const {
    conversations,
    isLoadingConversations,
    messages,
    isLoadingMessages,
    activeConversationUserId,
    messagingRestrictions,
    isCheckingMessagingStatus: _isCheckingMessagingStatus,
    setActiveConversation,
    loadConversations,
    loadMessages,
    checkMessagingStatus,
    checkUnreadMessages,
  } = useMessageStore();

  // Auto-fullscreen for mobile users when a conversation is selected
  useEffect(() => {
    if (isMobile() && activeConversationUserId) {
      setIsFullscreen(true);
    } else if (isMobile() && !activeConversationUserId) {
      setIsFullscreen(false);
    }
  }, [activeConversationUserId]);

  // Initialize data and set up real-time message listeners
  useEffect(() => {
    // Load conversations on mount
    loadConversations();
    // Check for unread messages when page loads
    checkUnreadMessages();
    
    // Cleanup: Clear active conversation when leaving the page
    return () => {
      setActiveConversation(null);
    };
  }, []);

  // Note: Socket listeners for direct messages are now handled globally in SocketContext
  // This ensures messages are received even when not on the messages page

  // Handle user selection from URL params
  useEffect(() => {
    if (selectedUserId && selectedUserId !== activeConversationUserId) {
      setActiveConversation(selectedUserId);
      // Only show mobile conversation on actual mobile screens
      setShowMobileConversation(window.innerWidth < 1024);
      
      // Check messaging status first
      checkMessagingStatus(selectedUserId).then((restriction) => {
        if (restriction.canMessage) {
          // User can message, load messages
          loadMessages(selectedUserId);
        }
        // Note: Messages will still be loaded for display even if user can't send new ones
        // This handles the case where users had previous conversations
      });
      
      // Check if we already have this conversation
      const existingConversation = conversations.find(conv => conv.user.id === selectedUserId);
      if (!existingConversation) {
        // This is a new conversation - fetch user profile
        fetchNewConversationUser(selectedUserId);
      } else {
        // Clear any existing new conversation user
        setNewConversationUser(null);
      }
    }
  }, [selectedUserId, activeConversationUserId, conversations]);

  // Fetch user profile for new conversations
  const fetchNewConversationUser = async (userId: string) => {
    try {
      setIsLoadingNewUser(true);
      const userProfile = await userService.getUserById(parseInt(userId));
      setNewConversationUser({
        id: userProfile.id.toString(),
        username: userProfile.username,
        profilePhoto: userProfile.profilePhoto
      });
    } catch (error) {
      console.error('Failed to fetch user profile for new conversation:', error);
      setNewConversationUser(null);
    } finally {
      setIsLoadingNewUser(false);
    }
  };

  // Handle conversation selection
  const handleConversationSelect = (userId: string) => {
    setSearchParams({ user: userId });
    setActiveConversation(userId);
    setShowMobileConversation(window.innerWidth < 1024);
    loadMessages(userId);
  };

  // Handle back to conversations (mobile)
  const handleBackToConversations = () => {
    setSearchParams({});
    setActiveConversation(null);
    setShowMobileConversation(false);
    setNewConversationUser(null);
    setIsFullscreen(false); // Exit fullscreen when going back
  };

  // Get current conversation user (from existing conversation or new conversation user)
  const currentConversationUser = activeConversationUserId 
    ? conversations.find(conv => conv.user.id === activeConversationUserId)?.user || newConversationUser
    : null;

  // Get messages for active conversation with grouping
  const currentMessages = activeConversationUserId 
    ? messages[activeConversationUserId] || []
    : [];
  
  // Apply message grouping
  const groupedCurrentMessages = groupConsecutiveMessages(currentMessages);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (activeConversationUserId && currentMessages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(scrollToBottom, 100);
    }
  }, [currentMessages.length, activeConversationUserId, showMobileConversation]);

  // Auto-scroll when conversation is loaded
  useEffect(() => {
    if (activeConversationUserId && currentMessages.length > 0 && !isLoadingMessages[activeConversationUserId]) {
      // Scroll to bottom when conversation is fully loaded
      setTimeout(scrollToBottom, 200);
    }
  }, [activeConversationUserId, isLoadingMessages]);

  // State for rendering

  // Mobile view - show conversation (including loading state for new conversations)
  if (showMobileConversation && activeConversationUserId && (currentConversationUser || isLoadingNewUser)) {
    return (
      <div
        className="h-[85vh] flex flex-col lg:hidden bg-surface border border-white/[.08] rounded-nav shadow-panel overflow-hidden"
        style={isFullscreen ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, borderRadius: 0, height: '100vh' } : {}}
      >{/* Mobile conversation view */}
        {currentConversationUser ? (
          <ConversationHeader
            user={currentConversationUser}
            onBack={handleBackToConversations}
            showBackButton={true}
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
          />
        ) : (
          <div className="p-4 border-b border-white/[.08]">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleBackToConversations}
                className="text-fg-muted hover:text-fg-bright transition-colors"
              >
                ←
              </button>
              <div className="animate-pulse">
                <div className="h-4 bg-white/10 rounded w-32"></div>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Messaging restriction warning */}
          {activeConversationUserId && messagingRestrictions[activeConversationUserId] && (
            <div className="p-4 border-b border-white/[.06]">
              <MessageRestrictionWarning restriction={messagingRestrictions[activeConversationUserId]} />
            </div>
          )}
          
          {/* Messages */}
          <div
            ref={mobileMessagesContainerRef}
            className="flex-1 overflow-y-auto p-4"
            style={{ background: 'radial-gradient(700px 300px at 100% 0%, rgba(122,61,255,.08), transparent)' }}
          >
            {isLoadingMessages[activeConversationUserId] && currentMessages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <LoadingSpinner />
              </div>
            ) : currentMessages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-neon-cyan" />
                  <p className="text-lg font-semibold mb-2 text-fg-bright">Nog geen berichten</p>
                  <p className="text-sm text-fg-muted">Start je gesprek door een bericht te typen!</p>
                </div>
              </div>
            ) : (
              groupedCurrentMessages.map((message, messageIndex) => (
                <DirectMessageBubble
                  key={message.id || `temp-dm-${message.senderId}-${message.sentAt}-${messageIndex}`}
                  directMessage={message}
                  currentUserId={user?.id?.toString() || ''}
                  isGrouped={(message as any).isGrouped}
                  isGroupLeader={(message as any).isGroupLeader}
                />
              ))
            )}
          </div>

          {/* Message Input */}
          {activeConversationUserId && (
            <DirectMessageInput
              receiverId={activeConversationUserId}
              disabled={!isConnected}
              messagingRestriction={messagingRestrictions[activeConversationUserId] || null}
            />
          )}
        </div>
      </div>
    );
  }

  // Desktop and mobile conversation list view
  return (
    <div className="h-[85vh] flex bg-surface border border-white/[.08] rounded-nav shadow-panel overflow-hidden"
         style={isFullscreen ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, borderRadius: 0, height: '100vh' } : {}}>
      {/* Conversation List - Left Panel */}
      <div className={`${showMobileConversation ? 'hidden' : 'block'} lg:${isFullscreen ? 'hidden' : 'block'} w-full lg:w-80 border-r border-white/[.08] flex flex-col`}>
        {/* Header */}
        <div className="p-4 border-b border-white/[.08]">
          <div className="sf-eyebrow mb-1.5">// berichten</div>
          <h1 className="text-xl font-bold tracking-tight text-fg-bright">Berichten</h1>
          <p className="text-sm text-fg-muted mt-1">Je gesprekken met andere gebruikers</p>
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          <ConversationList
            conversations={conversations}
            activeUserId={activeConversationUserId}
            onConversationSelect={handleConversationSelect}
            isLoading={isLoadingConversations}
          />
        </div>
      </div>

      {/* Conversation Panel - Right Panel (Desktop only or Fullscreen) */}
      <div className={`${isFullscreen ? 'flex' : 'hidden lg:flex'} flex-1 flex-col rounded-r-lg overflow-hidden`}>
        {activeConversationUserId && (currentConversationUser || isLoadingNewUser) ? (
          <>
            {currentConversationUser ? (
              <ConversationHeader 
                user={currentConversationUser}
                isFullscreen={isFullscreen}
                onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
              />
            ) : (
              <div className="p-4 border-b border-white/[.08]">
                <div className="animate-pulse">
                  <div className="h-4 bg-white/10 rounded w-32"></div>
                </div>
              </div>
            )}
            
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Messaging restriction warning */}
              {activeConversationUserId && messagingRestrictions[activeConversationUserId] && (
                <div className="p-4 border-b border-white/[.06]">
                  <MessageRestrictionWarning restriction={messagingRestrictions[activeConversationUserId]} />
                </div>
              )}
              
              {/* Messages */}
              <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-4"
                style={{ background: 'radial-gradient(700px 300px at 100% 0%, rgba(122,61,255,.08), transparent)' }}
              >
                {isLoadingMessages[activeConversationUserId] && currentMessages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <LoadingSpinner />
                  </div>
                ) : currentMessages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <MessageSquare className="h-12 w-12 mx-auto mb-4 text-neon-cyan" />
                      <p className="text-lg font-semibold mb-2 text-fg-bright">Nog geen berichten</p>
                      <p className="text-sm text-fg-muted">Start je gesprek door een bericht te typen!</p>
                    </div>
                  </div>
                ) : (
                  groupedCurrentMessages.map((message, messageIndex) => (
                    <DirectMessageBubble
                      key={message.id || `temp-dm-${message.senderId}-${message.sentAt}-${messageIndex}`}
                      directMessage={message}
                      currentUserId={user?.id?.toString() || ''}
                      isGrouped={(message as any).isGrouped}
                      isGroupLeader={(message as any).isGroupLeader}
                    />
                  ))
                )}
              </div>

              {/* Message Input */}
              {activeConversationUserId && (
                <DirectMessageInput
                  receiverId={activeConversationUserId}
                  disabled={!isConnected}
                  messagingRestriction={messagingRestrictions[activeConversationUserId] || null}
                />
              )}
            </div>
          </>
        ) : (
          // Welcome screen
          <div
            className="flex-1 flex items-center justify-center"
            style={{ background: 'radial-gradient(700px 300px at 100% 0%, rgba(122,61,255,.08), transparent)' }}
          >
            <div className="text-center max-w-md px-6">
              <MessageSquare className="h-16 w-16 mx-auto mb-6 text-neon-cyan" />
              <h2 className="text-xl font-bold tracking-tight mb-4 text-fg-bright">Welkom bij je berichten</h2>
              <p className="text-sm leading-relaxed mb-6 text-fg-body">
                Selecteer een gesprek aan de linkerkant om je berichten te bekijken, of start een nieuw gesprek
                door op de berichtknop te klikken bij een gebruikersprofiel in je groepen.
              </p>
              <div className="text-xs text-fg-muted">
                💡 Je kunt alleen berichten sturen naar gebruikers die je hebt ontmoet via groepen
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Connection status indicator */}
      {!isConnected && (
        <div className="fixed bottom-4 left-4 bg-surface border border-neon-pink/40 px-3 py-2 rounded-item text-sm shadow-panel">
          <span className="text-fg-bright font-medium">Niet verbonden met chat</span>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;